/**
 * Get device type based on user agent and screen size
 * @returns Device type string
 */
const getDeviceType = (): string => {
  const userAgent = navigator.userAgent.toLowerCase();
  const screenWidth = window.screen.width;

  // Check for mobile devices
  if (
    /mobile|android|iphone|ipad|ipod|blackberry|windows phone/i.test(userAgent)
  ) {
    if (
      /ipad/i.test(userAgent) ||
      (screenWidth >= 768 && screenWidth <= 1024)
    ) {
      return 'Tablet';
    }
    return 'Mobile';
  }

  // Check for tablets
  if (
    /tablet|ipad/i.test(userAgent) ||
    (screenWidth >= 768 && screenWidth <= 1024)
  ) {
    return 'Tablet';
  }

  return 'Desktop';
};

/**
 * Get browser name and version
 * @returns Browser name and version string
 */
const getBrowserInfo = (): string => {
  const userAgent = navigator.userAgent;

  // Chrome
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
    return match ? `Chrome ${match[1]}` : 'Chrome';
  }

  // Firefox
  if (userAgent.includes('Firefox')) {
    const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
    return match ? `Firefox ${match[1]}` : 'Firefox';
  }

  // Safari
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    const match = userAgent.match(/Version\/(\d+\.\d+)/);
    return match ? `Safari ${match[1]}` : 'Safari';
  }

  // Edge
  if (userAgent.includes('Edg')) {
    const match = userAgent.match(/Edg\/(\d+\.\d+)/);
    return match ? `Edge ${match[1]}` : 'Edge';
  }

  // Internet Explorer
  if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
    const match =
      userAgent.match(/MSIE (\d+\.\d+)/) || userAgent.match(/rv:(\d+\.\d+)/);
    return match ? `Internet Explorer ${match[1]}` : 'Internet Explorer';
  }

  return 'Unknown Browser';
};

/**
 * Extract basic metadata (device, browser) without geolocation
 * @returns Basic metadata object
 */
export const extractBasicMetadata = (): Record<string, unknown> => {
  const metadata: Record<string, unknown> = {};

  // Add browser/environment metadata
  metadata.browser = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  };

  // Extract device type
  metadata.device = getDeviceType();

  // Extract browser name and version
  metadata.browserInfo = getBrowserInfo();

  return metadata;
};

/**
 * Extract full metadata including geolocation from SurveyJS model
 * @param surveyModel - The SurveyJS model instance
 * @returns Metadata object with completion information
 */
export const extractSurveyMetadata = async (
  surveyModel: Record<string, unknown>
): Promise<Record<string, unknown>> => {
  const metadata: Record<string, unknown> = {};

  // Extract SurveyJS specific metadata
  if (surveyModel.startTime) {
    metadata.startTime = surveyModel.startTime;
  }

  if (surveyModel.endTime) {
    metadata.endTime = surveyModel.endTime;
  }

  if (surveyModel.timeSpent) {
    metadata.timeSpent = surveyModel.timeSpent;
  }

  // Extract page navigation data
  if (surveyModel.currentPageNo !== undefined) {
    metadata.currentPageNo = surveyModel.currentPageNo;
  }

  if (
    surveyModel.visiblePages &&
    Array.isArray(surveyModel.visiblePages) &&
    surveyModel.visiblePages.length > 0
  ) {
    metadata.totalPages = surveyModel.visiblePages.length;
  }

  // Extract question-level metadata
  const questionMetadata: Record<string, unknown> = {};
  if (typeof surveyModel.getAllQuestions === 'function') {
    const questions = surveyModel.getAllQuestions();
    if (Array.isArray(questions)) {
      questions.forEach((question: Record<string, unknown>) => {
        if (question.timeSpent && typeof question.name === 'string') {
          questionMetadata[question.name] = {
            timeSpent: question.timeSpent,
            isAnswered: question.isAnswered,
            isEmpty: question.isEmpty,
          };
        }
      });
    }
  }

  if (Object.keys(questionMetadata).length > 0) {
    metadata.questions = questionMetadata;
  }

  // Add browser/environment metadata
  metadata.browser = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  };

  // Extract device type
  metadata.device = getDeviceType();

  // Extract browser name and version
  metadata.browserInfo = getBrowserInfo();

  return metadata;
};
